import { useCallback, useEffect, useState } from "react";

const getLocalStorageItem = (storageKey?: string) => {
  if (!storageKey) {
    return {};
  }

  try {
    return JSON.parse(localStorage.getItem(storageKey) ?? "{}");
  } catch (caught) {
    return {};
  }
};

/**
 * @deprecated this hook is faulty. use the hook useStickyState() instead.
 */
export const useFilters = <TFilter,>(
  storageKey?: string
): [Partial<TFilter>, (newFilters: Partial<TFilter>) => void] => {
  const [filters, setFiltersInternal] = useState<Partial<TFilter>>(() =>
    getLocalStorageItem(storageKey)
  );

  const setFilters = useCallback(
    (newItem: Partial<TFilter>) => {
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(newItem));
        // this allows us to trigger updates in components that are not parents of this component
        dispatchEvent(
          new StorageEvent("storage", {
            key: storageKey,
          })
        );
      } else {
        setFiltersInternal(newItem);
      }
    },
    [storageKey]
  );

  useEffect(() => {
    if (!storageKey) {
      return;
    }

    const onStorageEvent = (event: StorageEvent) => {
      if (event.key === storageKey) {
        setFiltersInternal(getLocalStorageItem(storageKey));
      }
    };

    // in certain rendering cases, if this effect isn't fired, the listener
    // won't bind in time for a storage event firing from elsewhere in the
    // render tree, which means the state for this instance of the hook might go
    // out of sync with the contents inside storage
    window.addEventListener("storage", onStorageEvent);
    // so don't wait till the bind happens - sync immediately since the storage
    // is always the source of truth.
    setFiltersInternal(getLocalStorageItem(storageKey));

    return () => {
      window.removeEventListener("storage", onStorageEvent);
    };
  }, [storageKey]);

  return [filters, setFilters];
};
